import React from 'react';
import Button from './ui/Button';

/**
 * #204 — Prévia das reanálises parciais (6M→porquês e porquês→causa) em modal.
 *
 * Substitui o window.confirm nativo que exibia a nova causa raiz como parágrafo
 * denso num popup de sistema. Mostra causa ATUAL × PROPOSTA lado a lado + o
 * aviso de revisão das ações. Reusa as classes do CascataIAModal (NCResult.css)
 * — zero CSS novo. O fluxo preview→confirmar do backend não muda: este modal é
 * só a moldura do aceite.
 *
 * Props:
 *   preview    — { tipo: '6m'|'porques', causaAnterior, novaCausa,
 *                  porquesGerados?, avisoAcoes?,
 *                  acoesMergePreview?, acoesPreservadasIds?,
 *                  acoesRegeneradasCount? } (null = fechado)
 *   aceitando  — true enquanto a 2ª chamada (confirmarReanalise) roda
 *   onAceitar / onCancelar
 *
 * #189 P2 — quando o regen seletivo está ativo (flag env do backend ON), o
 * preview traz `acoesMergePreview` (como as ações ficarão após o aceite):
 * preservadas (editadas à mão, ids em acoesPreservadasIds) + novas da IA.
 * Sem os campos (flag off / sem ações elegíveis) a seção não renderiza —
 * degrade gracioso pro fluxo atual.
 */
function PreviewReanaliseModal({ preview, aceitando, onAceitar, onCancelar }) {
  if (!preview) return null;
  const {
    tipo, causaAnterior, novaCausa, porquesGerados, avisoAcoes,
    acoesMergePreview, acoesPreservadasIds, acoesRegeneradasCount,
  } = preview;

  const temRegenAcoes = Array.isArray(acoesMergePreview) && acoesMergePreview.length > 0;
  const idsPreservadas = new Set(acoesPreservadasIds || []);
  const descricaoAcao = (a) => a?.what || a?.descricao || a?.acao || '—';
  const prazoAcao = (a) => a?.prazo || a?.when || '';

  return (
    <div
      className="cascata-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="preview-reanalise-title"
      onClick={aceitando ? undefined : onCancelar}
    >
      <div className="cascata-modal-content" onClick={(e) => e.stopPropagation()}>
        <h3 id="preview-reanalise-title">
          <span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '6px' }}>autorenew</span>
          {tipo === '6m'
            ? 'Prévia — porquês e causa raiz a partir do 6M'
            : 'Prévia — causa raiz a partir dos porquês'}
        </h3>

        {causaAnterior ? (
          <div style={{ margin: '10px 0' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.04em', marginBottom: '4px' }}>
              CAUSA RAIZ ATUAL
            </div>
            <div style={{ padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', color: '#64748b', fontSize: '13px', lineHeight: 1.5 }}>
              {causaAnterior}
            </div>
          </div>
        ) : null}

        <div style={{ margin: '10px 0' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#006948', letterSpacing: '0.04em', marginBottom: '4px' }}>
            NOVA CAUSA RAIZ PROPOSTA
          </div>
          <div style={{ padding: '10px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', color: '#14532d', fontSize: '13.5px', lineHeight: 1.5 }}>
            {novaCausa}
          </div>
          {typeof porquesGerados === 'number' && porquesGerados > 0 && (
            <div style={{ fontSize: '12.5px', color: '#475569', marginTop: '6px' }}>
              {porquesGerados} porquê(s) regenerado(s) junto com a causa.
            </div>
          )}
        </div>

        {temRegenAcoes && (
          <div style={{ margin: '10px 0' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.04em', marginBottom: '4px' }}>
              AÇÕES CORRETIVAS APÓS O ACEITE
            </div>
            <div style={{ fontSize: '12px', color: '#475569', marginBottom: '6px' }}>
              {idsPreservadas.size > 0 && (
                <>{idsPreservadas.size} editada(s) à mão serão <strong>preservadas</strong> · </>
              )}
              {acoesRegeneradasCount || 0} serão <strong>atualizadas pela IA</strong> para a nova causa.
            </div>
            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
              {acoesMergePreview.map((a, i) => {
                const preservada = a?.id && idsPreservadas.has(a.id);
                return (
                  <div
                    key={a?.id || i}
                    style={{
                      padding: '8px 10px',
                      fontSize: '12.5px',
                      lineHeight: 1.45,
                      borderBottom: i < acoesMergePreview.length - 1 ? '1px solid #f1f5f9' : 'none',
                      background: preservada ? '#fafafa' : '#f0fdf4',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '10.5px',
                        fontWeight: 700,
                        padding: '1px 6px',
                        borderRadius: '8px',
                        marginRight: '6px',
                        background: preservada ? '#fef3cd' : '#dcfce7',
                        color: preservada ? '#856404' : '#14532d',
                      }}
                    >
                      {preservada ? 'preservada (editada à mão)' : 'nova (IA)'}
                    </span>
                    {descricaoAcao(a)}
                    {prazoAcao(a) ? (
                      <span style={{ color: '#94a3b8' }}> — {prazoAcao(a)}</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {avisoAcoes && (
          <div className="cascata-modal-warning">
            <span className="material-symbols-outlined" style={{ fontSize: '15px', verticalAlign: 'text-bottom', marginRight: '4px' }}>warning</span>
            <strong>{avisoAcoes}</strong>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
          <Button variant="secondary" onClick={onCancelar} disabled={aceitando}>
            Cancelar
          </Button>
          <Button icon={aceitando ? undefined : 'check'} onClick={onAceitar} disabled={aceitando}>
            {aceitando ? 'Aplicando…' : 'Aceitar reanálise'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PreviewReanaliseModal;
